import React, { useMemo, useState } from 'react';
import { Chip, LinearProgress, Tooltip, Typography } from '@mui/material';
import { type FirmwareReleasePackage } from 'api/admin/firmware/firmware';
import {
  type DeviceReleaseUpdateProgress,
  DeviceReleaseUpdateEventState,
  DeviceReleaseUpdateStatus,
} from 'api/admin/washboard/washboard';
import { formatWashboardTimestamp } from 'utils/formatTimestamp';
import './FirmwareUpdateProgress.scss';

type FirmwareUpdateProgressProps = {
  releaseVersion: string | null;
  packages: FirmwareReleasePackage[];
  releaseUpdate: DeviceReleaseUpdateProgress | null;
};

const STATUS_CHIP_CONFIG: Record<
  DeviceReleaseUpdateStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'error' }
> = {
  [DeviceReleaseUpdateStatus.PENDING]: { label: 'Update queued', color: 'default' },
  [DeviceReleaseUpdateStatus.IN_PROGRESS]: { label: 'Updating…', color: 'info' },
  [DeviceReleaseUpdateStatus.SUCCEEDED]: { label: 'Update succeeded', color: 'success' },
  [DeviceReleaseUpdateStatus.FAILED]: { label: 'Update failed', color: 'error' },
  [DeviceReleaseUpdateStatus.TIMED_OUT]: { label: 'Update timed out', color: 'error' },
  [DeviceReleaseUpdateStatus.CANCELLED]: { label: 'Update cancelled', color: 'default' },
};

const EVENT_STATE_LABELS: Record<DeviceReleaseUpdateEventState, string> = {
  [DeviceReleaseUpdateEventState.IDLE]: 'Idle',
  [DeviceReleaseUpdateEventState.DOWNLOAD]: 'Downloading firmware',
  [DeviceReleaseUpdateEventState.INSTALL]: 'Installing',
  [DeviceReleaseUpdateEventState.REBOOT_DELAY]: 'Waiting to reboot',
  [DeviceReleaseUpdateEventState.REBOOT]: 'Rebooting',
  [DeviceReleaseUpdateEventState.VERIFY]: 'Verifying update',
  [DeviceReleaseUpdateEventState.ROLLBACK]: 'Rolling back',
  [DeviceReleaseUpdateEventState.ERROR]: 'Error',
};

const parseProgressPercent = (progress: string | null): number | null => {
  if (!progress) {
    return null;
  }

  const [completed, total] = progress.split('/').map(Number);

  if (!total || Number.isNaN(completed) || Number.isNaN(total)) {
    return null;
  }

  return Math.round((completed / total) * 100);
};

const PackageList: React.FC<{ packages: FirmwareReleasePackage[] }> = ({ packages }) => {
  return (
    <div className="firmware-update-packages">
      <Typography variant="body2" sx={{ marginBottom: 0 }}>
        {`Packages (${packages.length}):`}
      </Typography>
      {!packages.length ? (
        <Typography variant="body2">No packages</Typography>
      ) : (
        <div className="firmware-update-packages-list">
          {packages.map((pkg) => (
            <Typography key={`${pkg.name}-${pkg.version}`} variant="body2">
              {`${pkg.name} ${pkg.version}`}
            </Typography>
          ))}
        </div>
      )}
    </div>
  );
};

const ProgressRow: React.FC<{ label: string; progress: string | null }> = ({
  label,
  progress,
}) => {
  if (!progress) {
    return null;
  }

  const percent = parseProgressPercent(progress);

  return (
    <div className="firmware-update-progress-row">
      <Typography variant="body2" className="firmware-update-progress-row-label">
        {`${label}: ${progress}`}
      </Typography>
      {percent !== null && <LinearProgress variant="determinate" value={percent} />}
    </div>
  );
};

const FirmwareUpdateProgress: React.FC<FirmwareUpdateProgressProps> = ({
  releaseVersion,
  packages,
  releaseUpdate,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const formattedUpdatedAt = useMemo(
    () => (releaseUpdate ? formatWashboardTimestamp(releaseUpdate.updatedAt) : null),
    [releaseUpdate],
  );

  const versionLabel = (
    <Tooltip
      title={<PackageList packages={packages} />}
      placement="bottom-start"
      slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [-5, 0] } }] } }}
    >
      <span className="firmware-version-value">{releaseVersion ?? 'N/A'}</span>
    </Tooltip>
  );

  if (!releaseUpdate) {
    return <span className="summary-value">{versionLabel}</span>;
  }

  const chipConfig = STATUS_CHIP_CONFIG[releaseUpdate.status];
  const isFailed =
    releaseUpdate.status === DeviceReleaseUpdateStatus.FAILED ||
    releaseUpdate.status === DeviceReleaseUpdateStatus.TIMED_OUT;

  return (
    <span className="summary-value firmware-update-progress">
      {versionLabel}
      <span
        className="firmware-update-chip-wrapper"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <span className="firmware-update-chip-anchor">
          <Chip
            label={chipConfig.label}
            color={chipConfig.color}
            size="small"
            className="firmware-update-chip"
          />
          {isOpen && <div className="firmware-update-hover-bridge" />}
        </span>
        {isOpen && (
          <div className="firmware-update-progress-detail">
            <Typography variant="subtitle2">
              {`Target version: ${releaseUpdate.targetReleaseVersion}`}
            </Typography>
            <Typography variant="body2">
              {`State: ${
                releaseUpdate.state ? EVENT_STATE_LABELS[releaseUpdate.state] : 'Unknown'
              }`}
            </Typography>
            <PackageList packages={releaseUpdate.packages} />
            <ProgressRow label="Download progress" progress={releaseUpdate.downloadProgress} />
            <ProgressRow label="Install progress" progress={releaseUpdate.installProgress} />
            {isFailed && releaseUpdate.errorText && (
              <Typography variant="body2" color="error">
                {releaseUpdate.errorText}
              </Typography>
            )}
            <Typography variant="caption" className="firmware-update-progress-timestamp">
              {`Last updated ${formattedUpdatedAt}`}
            </Typography>
          </div>
        )}
      </span>
    </span>
  );
};

export default FirmwareUpdateProgress;
